import ekoenergyFavicon from "@/assets/img/logo/ekoenergy-favicon.png";
import ekoenergyLogo from "@/assets/img/logo/ekoenergy-logo.png";
import solderaLogo from "@/assets/img/logo/logo-color.svg";

/**
 * Runtime white-label branding, keyed off window.location.hostname.
 *
 * One build serves every host; the hostname decides both the host wiring
 * (API base URL, Auth0 environment) and the visual brand (logo, title, theme).
 * The two are deliberately separate concepts: a dev override can swap the
 * visual brand for testing, but never the host wiring.
 */

export type BrandKey = "soldera" | "ekoenergy";

/**
 * Which portal shell a brand renders. "ecolabel" is the generic restricted
 * portal for ecolabel providers — a future provider (e.g. Naturemade) adds a
 * new Brand that points at the same portal.
 */
export type PortalKey = "soldera" | "ecolabel";

export interface HostConfig {
  brand: BrandKey;
  /** Axios baseURL; empty string routes through the Vite dev proxy. */
  apiBase: string;
  /** Selects the production Auth0 tenant. */
  isProductionHost: boolean;
}

export interface Brand {
  key: BrandKey;
  name: string;
  /** The portal shell (layout, sidenav, route surface) this brand renders. */
  portal: PortalKey;
  logo: string;
  title: string;
  /** Replaces the built-in favicon links; null keeps the Soldera icons. */
  favicon: string | null;
  /** Browser-chrome tint (PWA theme-color); null keeps the default. */
  themeColor: string | null;
  /**
   * Auth0 application (client) for this brand on the production tenant.
   * Brands without their own client (or non-production hosts) fall back to
   * the environment defaults in auth.ts.
   */
  productionAuthClientId: string | null;
}

const HOSTS: Record<string, HostConfig> = {
  "app.soldera.org": {
    brand: "soldera",
    apiBase: "https://api.soldera.org",
    isProductionHost: true,
  },
  "proov.soldera.org": {
    brand: "soldera",
    apiBase: "https://api.proov.soldera.org",
    isProductionHost: false,
  },
  "registry.ekoenergy.org": {
    brand: "ekoenergy",
    apiBase: "https://api.soldera.org",
    isProductionHost: true,
  },
};

// In development builds requests stay relative so they go through Vite's
// proxy (configured in vite.config.ts, respects BACKEND_PORT). The brand can
// be switched at config level with VITE_BRAND (e.g. `yarn serve:eko`); the
// host wiring is unaffected, so localhost never points at a production API.
const devHostConfig = (): HostConfig => ({
  brand: devBrand(),
  apiBase: "",
  isProductionHost: false,
});

const BRANDS: Record<BrandKey, Brand> = {
  soldera: {
    key: "soldera",
    name: "Soldera",
    portal: "soldera",
    logo: solderaLogo,
    title:
      "Soldera - Energy Attribute Certificates & Guarantees of Origin Management Platform | Login Portal",
    favicon: null,
    themeColor: null,
    productionAuthClientId: null,
  },
  ekoenergy: {
    key: "ekoenergy",
    name: "EKOenergy",
    portal: "ecolabel",
    logo: ekoenergyLogo,
    title: "EKOenergy Registry",
    favicon: ekoenergyFavicon,
    themeColor: "#24b24b",
    // registry.ekoenergy.org is registered in the Soldera Auth0 application's
    // callback/logout/web-origin lists, so the fallback client works on this
    // host (EKOenergy admins and sellers log in there today). Fill this in
    // only if a dedicated Auth0 application (own login-page branding) is
    // created for the portal — and keep the callback lists in sync when doing so.
    productionAuthClientId: null,
  },
};

/**
 * Resolves the host wiring (API base, Auth0 environment, brand) for the
 * current hostname. Development builds and unknown production hosts fall back
 * to the dev config: relative API URLs (Vite proxy) and the staging Auth0
 * tenant, with the brand chosen by the dev-only VITE_BRAND switch.
 */
export const getHostConfig = (): HostConfig => {
  if (!import.meta.env.PROD) {
    return devHostConfig();
  }
  // SECURITY: own-key check — a hostname like "constructor" must not resolve
  // through the object prototype.
  const hostname = window.location.hostname;
  return Object.hasOwn(HOSTS, hostname) ? HOSTS[hostname] : devHostConfig();
};

/**
 * Resolves the visual brand for the current host. On production hosts the
 * hostname alone decides; in development VITE_BRAND selects the brand while
 * the host wiring stays on the dev config.
 */
export const getBranding = (): Brand => BRANDS[getHostConfig().brand];

/**
 * True on any non-Soldera brand. Gates Soldera-only integrations (support
 * chat, Soldera-branded chrome) that must never surface on a white-label
 * host. Layout differences are NOT gated on this — they come from the portal
 * registry in `@/portals`.
 */
export const isWhiteLabel = (): boolean => getBranding().key !== "soldera";

/** Sets the document title, favicon, and CSS brand hook. Call once at startup. */
export const applyBranding = (): void => {
  const brand = getBranding();
  document.title = brand.title;
  document.documentElement.dataset.brand = brand.key;
  if (brand.favicon) {
    // index.html ships Soldera icons in several sizes/formats; point them all
    // at the brand icon so no fallback resolution can pick a Soldera one.
    document
      .querySelectorAll<HTMLLinkElement>(
        'link[rel="icon"], link[rel="apple-touch-icon"]'
      )
      .forEach((link) => {
        link.href = brand.favicon as string;
        link.removeAttribute("type");
      });
    // The Safari pinned-tab mask icon is a monochrome Soldera mark with no
    // brand equivalent; drop it so Safari falls back to the brand favicon.
    document
      .querySelectorAll('link[rel="mask-icon"]')
      .forEach((link) => link.remove());
  }
  if (brand.themeColor) {
    document
      .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
      ?.setAttribute("content", brand.themeColor);
  }
};

const isBrandKey = (value: string): value is BrandKey => value in BRANDS;

const devBrand = (): BrandKey => {
  const envBrand = import.meta.env.VITE_BRAND;
  return typeof envBrand === "string" && isBrandKey(envBrand)
    ? envBrand
    : "soldera";
};
